// src/components/GenerarDataJS.js
import React, { useState, useEffect } from 'react';
import {
  Box, Container, Typography, Button, CircularProgress, Paper, Alert, Snackbar, AppBar, Toolbar, Chip
} from '@mui/material';
import { Download as DownloadIcon, Refresh as RefreshIcon, CloudUpload as UploadIcon, Lock as LockIcon, Public, Security } from '@mui/icons-material';
import api from '../api/api';

const GenerarDataJS = () => {
  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [data, setData] = useState(null);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [jsContent, setJsContent] = useState('');
  const [jsPrivateContent, setJsPrivateContent] = useState('');
  const [progress, setProgress] = useState('');

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (token) {
      try {
        const payload = JSON.parse(atob(token.split('.')[1]));
        if (payload.id_pet) setTotal(0);
      } catch (err) { console.error(err); }
    }
  }, []);

  const fetchPacientes = async () => {
    setLoading(true); setError(null);
    try {
      const res = await api.get('/pacientes/online?limit=10000');
      const pacientes = res.data || [];
      setData(pacientes);
      setTotal(pacientes.length);
      setSnackbarMessage(`Cargados ${pacientes.length} pacientes con QR`);
      setSnackbarOpen(true);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message;
      setError(msg); setSnackbarMessage("Error: " + msg);
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

    const generarArchivos = async () => {
    if (!data || data.length === 0) return;
    setGenerating(true);
    setProgress("Generando QR público...");

    const publicObj = {};
    const privateObj = {};
    let count = 0;

    // --- QR PÚBLICO ---
    data.forEach(p => {
      if (p.qr_code) {
        publicObj[p.qr_code] = {
          n: p.nombre_paciente || '',
          r: p.nombre_raza || '',
          e: p.especie || '',
          s: p.sexo_paciente || '',
          c: p.castrado || '',
          mic: p.microchip || '',
          suc: p.nombre_sucursal || '',
          edad: p.edad_completa || '',
        };
      }
    });

    setProgress("Cargando recetas y vacunas privadas...");

    // --- QR PRIVADO CON PRÓXIMA VACUNA CORREGIDA ---
    for (const p of data) {
      count++;
      setProgress(`Procesando ${count}/${data.length}: ${p.nombre_paciente}`);

      if (!p.qr_code || !p.token_cliente || !p.id_paciente) continue;

      privateObj[p.qr_code] = {
        n: p.nombre_paciente,
        token: p.token_cliente,
        receta: "Sin receta registrada",
        vacunas: ["Sin registro de vacunas"],
        proxima: "No programada"
      };

      // === RECETA ÚLTIMA (SOLO SI TIENE MENOS DE 20 DÍAS) ===
      try {
        const rec = await api.get(`/recetas/?id_paciente=${p.id_paciente}&sucursal=1`);
        if (rec.data && rec.data.length > 0) {
          const ultimaReceta = rec.data[0];
          const fechaReceta = new Date(ultimaReceta.fecha_receta);
          const hoy = new Date();
          const diffDias = Math.floor((hoy - fechaReceta) / (1000 * 60 * 60 * 24));

          if (diffDias <= 20) {
            privateObj[p.qr_code].receta = ultimaReceta.detalle_receta?.trim() || "Sin detalles";
            privateObj[p.qr_code].fecha_receta = ultimaReceta.fecha_receta.slice(0,10); // opcional: mostrar fecha
          } else {
            privateObj[p.qr_code].receta = "Receta vencida (más de 20 días)";
            console.log(`Receta ignorada para ${p.nombre_paciente} - ${diffDias} días`);
          }
        } else {
          privateObj[p.qr_code].receta = "Sin receta registrada";
        }
      } catch (e) {
        privateObj[p.qr_code].receta = "Sin receta registrada";
      }

            // === TODOS LOS EXTRAS: VACUNAS + DESPARASITANTES + TERAPIAS ===
      try {
        // VACUNAS
        const vac = await api.get(`/citas/tabs?id_paciente=${p.id_paciente}&id_tab=VAC`);
        const aplicadasVac = vac.data.filter(v => v.estado_cita === "APL").map(v => `${v.fecha_cita.slice(0,10)}: ${v.descripcion}`).reverse();
        const proxVac = vac.data.filter(v => v.estado_cita === "PRG").map(v => ({ fecha: v.fecha_efectiva || v.fecha_cita })).sort((a,b) => new Date(a.fecha) - new Date(b.fecha))[0];
        if (aplicadasVac.length > 0) privateObj[p.qr_code].vacunas = aplicadasVac;
        if (proxVac) privateObj[p.qr_code].proxima = proxVac.fecha.slice(0,10);

        // DESPARASITANTES
        const des = await api.get(`/citas/tabs?id_paciente=${p.id_paciente}&id_tab=DES`);
        const aplicadasDes = des.data.filter(v => v.estado_cita === "APL").map(v => `${v.fecha_cita.slice(0,10)}: ${v.descripcion}`).reverse();
        const proxDes = des.data.filter(v => v.estado_cita === "PRG").map(v => ({ fecha: v.fecha_efectiva || v.fecha_cita })).sort((a,b) => new Date(a.fecha) - new Date(b.fecha))[0];
        privateObj[p.qr_code].desparasitantes = aplicadasDes.length > 0 ? aplicadasDes : ["Sin registro"];
        if (proxDes) privateObj[p.qr_code].proxima_desparasitante = proxDes.fecha.slice(0,10);

        // TERAPIAS
        const ter = await api.get(`/citas/tabs?id_paciente=${p.id_paciente}&id_tab=TER`);
        const aplicadasTer = ter.data.filter(v => v.estado_cita === "APL").map(v => `${v.fecha_cita.slice(0,10)}: ${v.descripcion}`).reverse();
        const proxTer = ter.data.filter(v => v.estado_cita === "PRG").map(v => ({ fecha: v.fecha_efectiva || v.fecha_cita })).sort((a,b) => new Date(a.fecha) - new Date(b.fecha))[0];
        privateObj[p.qr_code].terapias = aplicadasTer.length > 0 ? aplicadasTer : ["Sin registro"];
        if (proxTer) privateObj[p.qr_code].proxima_terapia = proxTer.fecha.slice(0,10);

      } catch (e) {}
    }

    // GENERAR ARCHIVOS
    const publicContent = `// data.js - QR Público - ${new Date().toLocaleString('es-SV')}\nconst pets = ${JSON.stringify(publicObj, null, 2)};\n`;
    const privateContent = `// private-data.js - QR Privado con Receta y Vacunas\n// Generado: ${new Date().toLocaleString('es-SV')}\nconst privatePets = ${JSON.stringify(privateObj, null, 2)};\n`;

    setJsContent(publicContent);
    setJsPrivateContent(privateContent);

    descargar(publicContent, `data_${new Date().toISOString().slice(0,10)}.js`);
    descargar(privateContent, `private-data_${new Date().toISOString().slice(0,10)}.js`);

    setSnackbarMessage(`¡LISTO! ${Object.keys(privateObj).length} QR privados con próxima vacuna corregida`);
    setSnackbarOpen(true);
    setProgress('');
    setGenerating(false);
  };

  const descargar = (content, filename) => {
    const blob = new Blob([content], { type: 'application/javascript' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
  };

  const subirPublico = async () => {
    if (!jsContent) return;
    setGenerating(true);
    try {
      await api.post('/pacientes/subir-datajs', { content: jsContent });
      setSnackbarMessage('QR PÚBLICO publicado – En 20 segundos en vivo');
    } catch (err) {
      setError("Error al publicar público: " + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false); setSnackbarOpen(true);
    }
  };

  const subirPrivado = async () => {
    if (!jsPrivateContent) return;
    setGenerating(true);
    try {
      await api.post('/pacientes/subir-private-datajs', { content: jsPrivateContent });
      setSnackbarMessage('QR PRIVADO publicado – Recetas y vacunas en vivo');
    } catch (err) {
      setError("Error al publicar privado: " + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false); setSnackbarOpen(true);
    }
  };

  const handleSnackbarClose = () => setSnackbarOpen(false);

  return (
    <Box sx={{ flexGrow: 1, bgcolor: '#f5f7fa', minHeight: '100vh' }}>
      <AppBar position="static" sx={{ bgcolor: '#1a3d7c' }}>
        <Toolbar>
          <Security sx={{ mr: 2 }} />
          <Typography variant="h6">Actualizar fichas QR pacientes</Typography>
        </Toolbar>
      </AppBar>

      <Container maxWidth="md" sx={{ mt: 5 }}>
        <Paper elevation={6} sx={{ p: 5, borderRadius: 3, bgcolor: 'white' }}>
          <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 'bold', color: '#1a3d7c' }}>
            QR Público + Privado (Receta y Vacunas)
          </Typography>
          <Typography align="center" color="text.secondary" sx={{ mb: 4 }}>
            Sistema de gestion de fichas electronicas Qr veterinaria – El Salvador 2025
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}
          {progress && <Alert severity="info" sx={{ mb: 3 }}>{progress}</Alert>}

          <Box sx={{ display: 'flex', justifyContent: 'center', gap: 3, flexWrap: 'wrap', mb: 4 }}>
            <Button
              variant="outlined"
              size="large"
              startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}
              onClick={fetchPacientes}
              disabled={loading}
            >
              {loading ? 'Cargando...' : 'Cargar Pacientes'}
            </Button>

            <Button
              variant="contained"
              size="large"
              color="success"
              startIcon={generating ? <CircularProgress size={20} /> : <DownloadIcon />}
              onClick={() => generarArchivos()}  // <-- ASÍ SÍ ESPERA
              disabled={!data || generating}
            >
              {generating ? 'Generando...' : `Generar Ambos (${total})`}
            </Button>

            <Button
              variant="contained"
              size="large"
              startIcon={<Public />}
              onClick={subirPublico}
              disabled={!jsContent || generating}
            >
              Publicar Público
            </Button>

            <Button
              variant="contained"
              size="large"
              color="error"
              startIcon={<LockIcon />}
              onClick={subirPrivado}
              disabled={!jsPrivateContent || generating}
            >
              Publicar Privado
            </Button>
          </Box>

          {data && (
            <Box sx={{ textAlign: 'center', p: 3, bgcolor: '#e3f2fd', borderRadius: 2 }}>
              <Chip label={total + " pacientes con QR"} color="primary" sx={{ fontSize: '1.1rem', px: 2 }} />
            </Box>
          )}
        </Paper>
      </Container>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleSnackbarClose} severity={error ? 'error' : 'success'} sx={{ fontSize: '1rem' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default GenerarDataJS;